import { PDFDocument } from 'pdf-lib';

import api from '../clients/api';

const service = {
	convertHtmlToPdfBlob,
	convertHtmlToPdfBlobDocumentEngine,
	convertHtmlToPdfBlobDocumentEnginePublic,
	mergePdfBlobsToBuffer,
};

/**
 * Converts HTML to PDF blob via the Document Engine
 * @param input - Either an HTML string or an object with elementId to extract HTML from DOM
 * @param accessToken - Public access token for the document
 * @returns Promise<Blob> - PDF blob
 */
async function convertHtmlToPdfBlob(
	input: string | { elementId: string },
	accessToken?: string
): Promise<Blob> {
	let html: string;

	// Extract HTML based on input type
	if (typeof input === 'string') {
		html = input;
	} else {
		const container = document.getElementById(input.elementId);
		if (!container) throw new Error(`Element with ID '${input.elementId}' not found.`);
		html = container.innerHTML;
	}

	if (accessToken) {
		return convertHtmlToPdfBlobDocumentEnginePublic(html, accessToken);
	}
	return convertHtmlToPdfBlobDocumentEngine(html);
}

/**
 * Converts HTML to PDF using PSPDFKit Document Engine
 * This method provides professional-grade PDF generation with proper emoji and font support
 * @param html - HTML string to convert
 * @returns Promise<Blob> - PDF blob
 */
async function convertHtmlToPdfBlobDocumentEngine(html: string): Promise<Blob> {
	return api.document.convertHtmlToPdf(html);
}

/**
 * Converts HTML to PDF using PSPDFKit Document Engine
 * This method provides professional-grade PDF generation with proper emoji and font support
 * @param html - HTML string to convert
 * @param accessToken - Public access token for the document
 * @returns Promise<Blob> - PDF blob
 */
async function convertHtmlToPdfBlobDocumentEnginePublic(
	html: string,
	accessToken: string
): Promise<Blob> {
	return api.document.convertHtmlToPdfPublic(html, accessToken);
}

/**
 * Merges multiple PDF blobs into a single PDF buffer
 * @param partBlobs - Array of PDF blobs to merge
 * @param title - Title for the merged PDF document
 * @returns Promise<Uint8Array> - Merged PDF as buffer
 */
async function mergePdfBlobsToBuffer(partBlobs: Blob[], title: string): Promise<Uint8Array> {
	const mergedPdf = await PDFDocument.create();

	for (const blob of partBlobs) {
		const arrayBuffer = await blob.arrayBuffer();
		const pdf = await PDFDocument.load(arrayBuffer);
		const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
		// biome-ignore lint/suspicious/useIterableCallbackReturn: pre-existing callback shape; changing returns needs per-site behavior review
		copiedPages.forEach((page) => mergedPdf.addPage(page));
	}

	mergedPdf.setTitle(title);
	mergedPdf.setAuthor('');

	const pdfBytes = await mergedPdf.save();

	return pdfBytes;
}

export default service;
